/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import CustomToast from "../../../../../widgets/toaster";
import {
	MBReconJustification,
	MBReconPayload,
	ReconMediaBatchDetailResponse,
	ReconMediaBatchDetails,
	ReconMediaBatchResource,
} from "../reconcileMediaBatch.types";
import { useEffect, useState } from "react";
import { mediaReconciliationService } from "../../../../../service/mediaReconciliationServices";
import { getMediaBatchReconDetailCols } from "../partials/columns";
import MediaPrepTable from "../../../../common/commonComponents/lotDetailsTable";
import ReconMediaDetailHeader from "../partials/ReconMediaDetailHeader";
import ReconMediaDetailStatCard from "../partials/ReconMediaDetailStatCard";
import MBYieldAccuracyAuditCard from "../partials/MBYieldAccuracyAuditCard";
import MBTechnicialVerificationCard from "../partials/MBTechnicialVerificationCard";
import MBReconStatusCard from "../partials/MBReconStatusCard";
import MBFooter from "../partials/MBFooter";

type Props = {
	batch: ReconMediaBatchDetails;
	goBack: () => void;
};

const ReconMediaBatchDetailView: React.FC<Props> = ({ goBack, batch }) => {
	const { t } = useTranslation();
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const [toastType, setToastType] = useState("");

	const showToastMessage = (message: string, type: string) => {
		setToastMessage(message);
		setToastType(type);
		setShowToast(true);
	};
	const [technicianName, setTechnicianName] = useState<string>("NA");
	const [mediaBatchIdentifier, setMediaBatchIdentifier] = useState<string>("");
	const [mediaBatchId, setMediaBatchId] = useState<string>("");
	const [mediaBatchResources, setMediaBatchResources] = useState<
		ReconMediaBatchResource[]
	>([]);
	const [stats, setStats] = useState({
		reconPercentage: 0,
		reconText: "NA",
		producedPlates: "0",
		volume: "0",
		powder: "0",

		targetPlates: 0,
		actualDispensed: 0,
		variance: 0,
	});
	const [justifications, setJustifications] = useState<
		Record<string, MBReconJustification>
	>({});

	const getMediaBatchResources = async () => {
		try {
			const response: ReconMediaBatchDetailResponse =
				await mediaReconciliationService.getReconMediaBatchDetailResources({
					batch_id: batch.batch_id,
				});
			setMediaBatchResources(response.data.resources);
			if (response.data.existing_reconciliation) {
				setTechnicianName(
					response.data.existing_reconciliation.first_name +
						" " +
						response.data.existing_reconciliation.last_name,
				);
			}
			setMediaBatchIdentifier(
				response.data.batch_info.media_batch_identifier,
			);
			const targetPlates = Number(response.data.recon_summary.target_plates,);
			const actualDispensed = Number(response.data.recon_summary.actual_dispensed,);

			setStats({
				reconPercentage: response.data.recon_summary.recon_percentage,
				reconText: response.data.recon_summary.tolerance_status,
				producedPlates: response.data.recon_summary.produced_plates,
				volume: response.data.solution_resources[0]?.actual_quantity,
				powder: response.data.powder_resources[0]?.actual_quantity,
				targetPlates,
				actualDispensed,
				variance: actualDispensed - targetPlates,
			});
			setMediaBatchId(response.data.preparation.media_batch_id);
		} catch (err) {
			console.log(err);
		}
	};
	const hasDeviation = (row: ReconMediaBatchResource) => {
		return Number(row.actual_quantity) !== Number(row.planned_quantity);
	};

	const hasUnfilledJustifications = mediaBatchResources
		.filter((row) => hasDeviation(row))
		.some((row) => !justifications[row.lot_reference]?.deviation_reason);

	// const handlePost = async () => {
	// 	if (hasUnfilledJustifications) return;
	// 	const payload: MBReconPayload = {
	// 		media_batch_id: mediaBatchId,
	// 		justifications: Object.values(justifications),
	// 	};
	// 	try {
	// 		const result =
	// 			await mediaReconciliationService.postMediaReconciliationBatch(
	// 				payload,
	// 			);
	// 		console.log(result);
	// 	} catch (err) {
	// 		console.log(err);
	// 	}
	// };

	const handlePost = async () => {
		if (hasUnfilledJustifications) return;
		const payload: MBReconPayload = {
			media_batch_id: mediaBatchId,
			justifications: Object.values(justifications),
		};
		try {
			const response =
				await mediaReconciliationService.postMediaReconciliationBatch(
					payload,
				);
			if (response.status && response.statusCode === 200) {
				showToastMessage(response.message, "success");

				setTimeout(() => {
					goBack();
				}, 2500);
			} else {
				showToastMessage(
					response.message,
					"error",
				);
			}
		} catch (error: any) {
			showToastMessage(
				error?.response?.data?.message,
				"error",
			);
		}
	};

	const handleReasonChange = (
		ref: string,
		deviation_reason: string,
		resource_type: string,
	) => {
		setJustifications({
			...justifications,
			[ref]: {
				lot_reference: ref,
				deviation_reason: deviation_reason,
				resource_type: resource_type,
			},
		});
	};

	useEffect(() => {
		getMediaBatchResources();
	}, []);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaReconcile.title")} />
			<div className="flex flex-col h-full bg-[#F3F6F9]">
				<ReconMediaDetailHeader goBack={goBack} />
				<div className="p-8 max-w-7xl mx-auto flex-1 overflow-y-auto space-y-6 animate-in fade-in duration-500 relative pb-32">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
						<ReconMediaDetailStatCard
							label={t("mediaReconcile.mediaBatch.inputPowder")}
							val={stats.powder + "g"}
							status="Accurate"
							icon="inventory_2"
						/>
						<ReconMediaDetailStatCard
							label={t("mediaReconcile.mediaBatch.targetVolume")}
							val={stats.volume + "mL"}
							status="Matched"
							icon="water_drop"
						/>
						<ReconMediaDetailStatCard
							label={t("mediaReconcile.mediaBatch.producedPlates")}
							val={stats.producedPlates}
							status="Units"
							icon="grid_view"
						/>
						<ReconMediaDetailStatCard
							label={t("mediaReconcile.mediaBatch.reconResult")}
							val={String(stats.reconPercentage) + "%"}
							status={stats.reconText}
							icon="analytics"
							color="text-emerald-500"
						/>
					</div>
					<div className="grid grid-cols-12 gap-8 items-start">
						<div className="col-span-12 lg:col-span-8 space-y-6">
							<div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
								<div className="px-6 py-4 bg-[#2D3E50] flex justify-between items-center">
									<h3 className="text-[10px] font-black text-white uppercase tracking-widest m-0">
										{t(
											"mediaReconcile.mediaBatch.resourceConsumptionLedger",
										)}
									</h3>
									<div className="flex items-center gap-2">
										<span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
										<span className="text-[9px] font-bold text-white/50 uppercase">
											{t(
												"mediaReconcile.mediaBatch.inventoryLinksActive",
											)}
										</span>
									</div>
								</div>

								<MediaPrepTable
									columns={getMediaBatchReconDetailCols(
										t,
										handleReasonChange,
										justifications,
									)}
									data={mediaBatchResources}
									emptyMessage={t("mediaReconcile.noDataToDisplay")}
								/>
							</div>
							<MBYieldAccuracyAuditCard
								theoreticalMax={stats.targetPlates}
								finalActual={stats.actualDispensed}
								variance={stats.variance}
							/>
						</div>
						<div className="col-span-12 lg:col-span-4 space-y-6">
							<MBTechnicialVerificationCard
								technicianName={technicianName}
								mediaBatchIdentifier={mediaBatchIdentifier}
							/>
							<MBReconStatusCard />
						</div>
					</div>
					<MBFooter
						handlePost={handlePost}
						disabled={Object.keys(justifications).length === 0}
						accuracy={stats.reconPercentage}
					/>
				</div>
			</div>
			<CustomToast
				show={showToast}
				onClose={() => setShowToast(false)}
				delay={2500}
				message={toastMessage}
				toastType={toastType}
			/>
		</div>
	);
};

export default ReconMediaBatchDetailView;
